// AI Assistant Service for Customer Web
// Handles communication with the AI Assistant microservice

const AI_ASSISTANT_BASE_URL = process.env.REACT_APP_AI_SERVICE_URL || 'http://35.154.34.137:3009/api/v1';

export interface ConversationRequest {
  message: string;
  user_id: string;
  session_id: string;
  language?: string;
  include_voice?: boolean;
}

export interface ConversationResponse {
  response: string;
  intent: string;
  entities: Array<{
    name: string;
    value: string;
    confidence: number;
  }>;
  suggestions: string[];
  action: string;
  session_id: string;
  language: string;
  audio_url?: string;
}

export interface LanguageDetectionRequest {
  text: string;
}

export interface LanguageDetectionResponse {
  language: string;
  confidence: number;
}

export interface TranslationRequest {
  text: string;
  target_language: string;
  source_language?: string;
}

export interface TranslationResponse {
  translated_text: string;
  source_language: string;
  target_language: string;
  confidence: number;
}

export interface SpeechToTextResponse {
  text: string;
  confidence: number;
  language: string;
}

export interface ProductSearchRequest {
  query: string;
  language?: string;
  filters?: Record<string, any>;
  limit?: number;
  offset?: number;
}

export interface ProductSearchResponse {
  products: any[];
  total: number;
  query: string;
  language: string;
  suggestions: string[];
}

export interface OrderRequest {
  user_id: string;
  items: Array<{
    product_id: string;
    quantity: number;
    price: number;
  }>;
  language?: string;
  delivery_address?: Record<string, any>;
  payment_method?: string;
}

export interface OrderResponse {
  order_id: string;
  status: string;
  total_amount: number;
  items: any[];
  estimated_delivery: string;
  tracking_info: Record<string, any>;
  language: string;
}

class AIAssistantService {
  private baseUrl: string;

  constructor(baseUrl: string = AI_ASSISTANT_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  // Conversation Management
  async sendMessage(request: ConversationRequest): Promise<ConversationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/conversation/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  async sendVoiceMessage(
    audioBlob: Blob,
    userId: string,
    sessionId: string,
    language: string = 'en'
  ): Promise<ConversationResponse> {
    try {
      // First convert speech to text
      const formData = new FormData();
      formData.append('audio', audioBlob);

      const speechResponse = await fetch(
        `${this.baseUrl}/voice/speech-to-text?language=${language}`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (!speechResponse.ok) {
        throw new Error('Speech recognition failed');
      }

      const speechData: SpeechToTextResponse = await speechResponse.json();

      if (!speechData.text) {
        throw new Error('No text recognized from audio');
      }

      // Then send the text as a regular message
      return await this.sendMessage({
        message: speechData.text,
        user_id: userId,
        session_id: sessionId,
        language,
        include_voice: true
      });
    } catch (error) {
      console.error('Error processing voice message:', error);
      throw error;
    }
  }

  // Language Processing
  async detectLanguage(text: string): Promise<LanguageDetectionResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/language/detect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error detecting language:', error);
      throw error;
    }
  }

  async translateText(
    text: string,
    targetLanguage: string,
    sourceLanguage?: string
  ): Promise<TranslationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/language/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          target_language: targetLanguage,
          source_language: sourceLanguage
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error translating text:', error);
      throw error;
    }
  }

  async getSupportedLanguages(): Promise<{ languages: Array<{ code: string; name: string }> }> {
    try {
      const response = await fetch(`${this.baseUrl}/language/supported`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting supported languages:', error);
      throw error;
    }
  }

  // Voice Processing
  async speechToText(audioBlob: Blob, language: string = 'en'): Promise<SpeechToTextResponse> {
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob);

      const response = await fetch(
        `${this.baseUrl}/voice/speech-to-text?language=${language}`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error converting speech to text:', error);
      throw error;
    }
  }

  async textToSpeech(text: string, language: string = 'en'): Promise<Blob> {
    try {
      const response = await fetch(`${this.baseUrl}/voice/text-to-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          language
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Error converting text to speech:', error);
      throw error;
    }
  }

  // Product Integration
  async searchProducts(request: ProductSearchRequest): Promise<ProductSearchResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/products/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  async browseProducts(
    category?: string,
    language: string = 'en',
    limit: number = 20,
    offset: number = 0
  ): Promise<ProductSearchResponse> {
    try {
      const params = new URLSearchParams({
        language,
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (category) {
        params.append('category', category);
      }

      const response = await fetch(`${this.baseUrl}/products/browse?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error browsing products:', error);
      throw error;
    }
  }

  async getCategories(language: string = 'en'): Promise<{ categories: any[]; language: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/products/categories?language=${language}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting categories:', error);
      throw error;
    }
  }

  // Order Management
  async createOrder(request: OrderRequest): Promise<OrderResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/orders/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  async getOrderStatus(orderId: string, language: string = 'en'): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/orders/status/${orderId}?language=${language}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting order status:', error);
      throw error;
    }
  }

  async getOrderHistory(
    userId: string,
    language: string = 'en',
    limit: number = 10
  ): Promise<any> {
    try {
      const response = await fetch(
        `${this.baseUrl}/orders/history/${userId}?language=${language}&limit=${limit}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting order history:', error);
      throw error;
    }
  }

  async trackDelivery(orderId: string, language: string = 'en'): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/orders/track/${orderId}?language=${language}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error tracking delivery:', error);
      throw error;
    }
  }

  // Personalization
  async getPersonalizedRecommendations(
    userId: string,
    language: string = 'en',
    limit: number = 10,
    category?: string
  ): Promise<any> {
    try {
      const params = new URLSearchParams({
        language,
        limit: limit.toString()
      });

      if (category) {
        params.append('category', category);
      }

      const response = await fetch(
        `${this.baseUrl}/personalization/recommendations/${userId}?${params}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting recommendations:', error);
      throw error;
    }
  }

  async getUserInsights(userId: string, language: string = 'en'): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/personalization/insights/${userId}?language=${language}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting user insights:', error);
      throw error;
    }
  }

  // Context Management
  async getConversationContext(userId: string, sessionId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/conversation/context/${userId}/${sessionId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting conversation context:', error);
      throw error;
    }
  }

  async clearConversationContext(userId: string, sessionId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/conversation/context/${userId}/${sessionId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error clearing conversation context:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const aiAssistantService = new AIAssistantService();
export default aiAssistantService;

